import { oc } from "@orpc/contract";
import { z } from "zod";

export const DivisionOptionSchema = z.object({
  code: z.string(),
  label: z.string(),
});

export const GetReportDivisionsResponseSchema = z.object({
  divisions: z.array(DivisionOptionSchema),
});

export const AdminReportType = z.enum(["staged", "staged_internal"]);

/** How the staged / internal-staged reports split their output. */
export const ReportFileGrouping = z.enum(["single", "per_division"]);

export const DownloadTransactionsReportInputSchema = z
  .object({
    reportType: AdminReportType.default("staged"),
    startDate: z.string().date(),
    endDate: z.string().date(),
    divisions: z.array(z.string()).min(1),
    fileGrouping: ReportFileGrouping.default("per_division"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

const ReportFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionCode: z.string(),
  count: z.number(),
});

export const DownloadTransactionsReportResponseSchema = z.object({
  files: z.array(ReportFileSchema),
});

export const DownloadClaimResponseReportInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    /** Division codes to include. Empty = all divisions. */
    divisions: z.array(z.string()).default([]),
    /** Optional supplier name / trading-partner code (ILIKE match). */
    supplier: z.string().optional(),
    fileGrouping: ReportFileGrouping.default("single"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

/** Transaction statuses selectable in the GL Posting report. */
export const GlPostingReportStatus = z.enum(["staged", "posted", "sent"]);

/**
 * GL Posting report — generates the journal-entry workbook (the same one the
 * Staged page produces) for transactions staged within a date range and in the
 * selected statuses. The date range matches each row's staged date (when we
 * processed/staged it) — NOT when the source transaction first arrived.
 *
 * Side effect: downloading also POSTS any selected `staged` rows (status
 * `staged` → `posted`), freezing them so no user can edit, unstage, or
 * mark-as-no-claim them. `sent` and already-`posted` rows are left untouched.
 * Staged rows with no GL configured can't produce a JE line, so they stay
 * staged and are surfaced as warnings (consistent with "Post All Staged").
 * When `staged` is not among the selected `statuses`, nothing is posted — the
 * download is a read-only re-generation of an existing posting.
 *
 * The `postTransactions` flag (admin-controlled toggle) gates the posting side
 * effect entirely: when `false`, the workbook is generated and returned WITHOUT
 * changing any transaction statuses (`postedCount` is always 0). When `true`
 * (default, preserves prior behavior), selected `staged` rows are posted/frozen.
 */
export const DownloadGlPostingReportInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    divisions: z.array(z.string()).min(1),
    /** Statuses to include. Defaults to all three (preserves prior behavior). */
    statuses: z
      .array(GlPostingReportStatus)
      .min(1)
      .default(["staged", "posted", "sent"]),
    /** "single" = one workbook (divisions on separate tabs); "per_division" = one file each. */
    fileGrouping: ReportFileGrouping.default("single"),
    /**
     * When true (default), posts selected `staged` rows (→ `posted`, frozen) as
     * a side effect of the download. When false, downloads the report only and
     * leaves all transaction statuses untouched.
     */
    postTransactions: z.boolean().default(true),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

const GlPostingFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionKey: z.string(),
});

const GlPostingWarningSchema = z.object({
  divisionKey: z.string(),
  message: z.string(),
  /**
   * How the UI should surface this. `error` means real dollars were NOT booked
   * because of a configuration gap (e.g. a branch has no 849 Rejected GL
   * account), so the workbook is incomplete and the operator must act — those
   * get a persistent error surface rather than a transient warning toast.
   * Defaults to `warning` so callers that don't classify keep today's behavior.
   */
  severity: z.enum(["warning", "error"]).default("warning"),
});

export const DownloadGlPostingReportResponseSchema = z.object({
  files: z.array(GlPostingFileSchema),
  warnings: z.array(GlPostingWarningSchema),
  /** Number of staged rows that were posted (frozen) by this download. */
  postedCount: z.number(),
});

/**
 * GL Posting report for EDI 849 responses. For every 844 claim line we got an
 * EDI 849 back for (in the selected date range / divisions), it books the
 * supplier's decision against per-branch accounts: it reverses the original
 * claim (DR 4014-00 / CR 2137), then re-applies per the 849 (DR 2137 approved,
 * CR 4014-00 the ORIGINAL claim, DR 4014-05 the difference between the
 * original claim and what the supplier approved — booked even when the 849
 * status is "accepted" but the credited amount differs). Read-only — no
 * posting side effect. Filtered by the 849 response received date.
 */
export const DownloadGl849PostingReportInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    /** Division codes to include. Empty = all divisions. */
    divisions: z.array(z.string()).default([]),
    /** Optional supplier short name / trading-partner code (ILIKE match). */
    supplier: z.string().optional(),
    /** "single" = one workbook (divisions on separate tabs); "per_division" = one file each. */
    fileGrouping: ReportFileGrouping.default("single"),
    /**
     * Detail-only "rejection report": only the Detail tab(s), covering every
     * matched 844/849 line in the period (including lines whose branch has no
     * GL configured), with no JE / Skipped / Unmatched / sample tabs.
     */
    detailOnly: z.boolean().default(false),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

const Gl849PostingFileSchema = z.object({
  excel: z.string(),
  filename: z.string(),
  divisionKey: z.string(),
  blockCount: z.number(),
});

export const DownloadGl849PostingReportResponseSchema = z.object({
  files: z.array(Gl849PostingFileSchema),
  warnings: z.array(GlPostingWarningSchema),
});

export const sdAdminReportsContracts = {
  getDivisions: oc
    .output(GetReportDivisionsResponseSchema)
    .route({ method: "GET" }),
  downloadTransactionsReport: oc
    .input(DownloadTransactionsReportInputSchema)
    .output(DownloadTransactionsReportResponseSchema)
    .route({ method: "POST" }),
  downloadClaimResponseReport: oc
    .input(DownloadClaimResponseReportInputSchema)
    .output(DownloadTransactionsReportResponseSchema)
    .route({ method: "POST" }),
  downloadGlPostingReport: oc
    .input(DownloadGlPostingReportInputSchema)
    .output(DownloadGlPostingReportResponseSchema)
    .route({ method: "POST" }),
  downloadGl849PostingReport: oc
    .input(DownloadGl849PostingReportInputSchema)
    .output(DownloadGl849PostingReportResponseSchema)
    .route({ method: "POST" }),
} as const;
